<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  report: { type: Object, required: true },
});

const { t } = useI18n();
const iframeRef = ref(null);
const isExporting = ref(false);

const formattedDate = computed(() => {
  if (!props.report.generated_at) return '';
  return new Date(props.report.generated_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
});

// Strip executable content before injecting into srcdoc.
function sanitizeHtml(html) {
  const preSanitized = (html || '')
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<\/?(iframe|object|embed|frame|frameset)\b[^>]*>/gi, '');

  const parser = new DOMParser();
  const doc = parser.parseFromString(preSanitized, 'text/html');

  doc.querySelectorAll('script').forEach(node => node.remove());

  doc.querySelectorAll('*').forEach(el => {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase();
      const value = (attr.value || '').trim().toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
      if (
        (name === 'href' || name === 'src') &&
        value.startsWith('javascript:')
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });

  const styleTags = Array.from(doc.querySelectorAll('style'))
    .map(node => node.outerHTML)
    .join('\n');
  doc.querySelectorAll('style').forEach(node => node.remove());

  return `${styleTags}${doc.body.innerHTML}`;
}

const srcdocContent = computed(() => {
  const cleanHtml = sanitizeHtml(props.report.html || '');
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:;">
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1a1a2e; font-size: 14px; line-height: 1.6; overflow: auto; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>${cleanHtml}</body>
</html>`;
});

async function exportPdf() {
  if (isExporting.value || !iframeRef.value) return;
  isExporting.value = true;
  try {
    iframeRef.value.contentWindow.focus();
    iframeRef.value.contentWindow.print();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[html-report] Print failed:', e);
  } finally {
    isExporting.value = false;
  }
}
</script>

<template>
  <div class="w-full rounded-xl ring-1 ring-n-weak overflow-hidden my-4">
    <!-- Export button bar -->
    <div
      class="flex items-center justify-between px-4 py-2 bg-n-solid-2 border-b border-n-weak"
    >
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('COPILOT.REPORT.GENERATED') }} {{ formattedDate }}
      </span>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand-hover transition-colors disabled:opacity-50"
        :disabled="isExporting"
        @click="exportPdf"
      >
        <span class="i-lucide-download size-3.5" />
        {{
          isExporting
            ? t('COPILOT.REPORT.EXPORTING')
            : t('COPILOT.REPORT.DOWNLOAD_PDF')
        }}
      </button>
    </div>

    <!-- Iframe-rendered report -->
    <iframe
      ref="iframeRef"
      sandbox="allow-same-origin allow-modals"
      :srcdoc="srcdocContent"
      class="w-full border-0 bg-white h-[72vh] min-h-[420px]"
    />
  </div>
</template>
